import {
  Box,
  Button,
  DataList,
  Dialog,
  Flex,
  FlexItem,
  Separator,
  SvgIcon
} from '@volue/wave-react';
import React from 'react';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import { SaveCalculationSetExecutionReport } from '@/features/build-calculation-context/components/Home/components/SelectedFeaturePanel/SaveCalculationSetExecutionReport';
import type { CompletedCalculationTactic } from '@/features/execute-calculation-set/overmind/calculation';
import { useActions, useAppState } from '@/overmind';

import { CollapsibleContainer, isVisible } from '../CollapsibleCard';
import { GridVersionSection } from './GridVersionSection';

export function CalculationHeader() {
  const isShowingGridElementDetailsEnabled =
    useFeatureToggles().isShowingGridElementTitleEnabled();
  const selected = useAppState().selectedFeature.current;
  const calculationTactic = useAppState().calculation.calculationTactic.value;
  const gridVersionName =
    useAppState().calculation.gridVersionName ?? 'default';
  const shareCalculationSetExecution =
    useActions().calculation.shareCalculationSetExecution;
  const isCalculationTacticResultUnavailable =
    useAppState().calculation.isCalculationTacticResultUnavailable;
  const calculationSetStatistics =
    useAppState().calculation.calculationSetStatistics;

  if (isCalculationTacticResultUnavailable) return null;
  if (calculationSetStatistics.type === 'NO_STATISTICS') return null;

  const tacticResult = (
    calculationTactic as unknown as CompletedCalculationTactic
  ).calculationSetResult;
  const tacticName = tacticResult.calculationSetName;
  const featureName = selected?.gc.title ?? 'Unnamed';
  const featureWebMapTitle = selected?.gc.webMapTitle ?? featureName;

  return (
    <span
      data-component={CalculationHeader.name}
      style={{ display: 'contents' }}
    >
      <Dialog>
        <h4 className="dF aiC">
          {isShowingGridElementDetailsEnabled ? (
            <>
              {tacticName} - {featureWebMapTitle}
            </>
          ) : (
            `${tacticName} - ${featureName}`
          )}
          <Dialog.Trigger
            as={Button}
            variant="ghost"
            css={{ fontSize: 'smaller', marginLeft: '$spacingM' }}
          >
            <Flex main={'center'} cross="center">
              <SvgIcon iconName="info" />
              <span>{'Calculation set statistics'}</span>
            </Flex>
          </Dialog.Trigger>
        </h4>
        <ShareExecutionButton
          shareCalculationSetExecution={shareCalculationSetExecution}
          className="mHxs"
        />
        <SaveCalculationSetExecutionReport
          text="Save execution report as JSON"
          variant="outline"
          className="mHxs"
        />
        <Dialog.Box width={'50rem'}>
          <Dialog.Header>
            <Box css={{ color: '$colorAccent70', fontSize: 'larger' }}>
              {tacticName}
            </Box>
            <Dialog.Close
              as={Button}
              variant="ghost"
              shape="circle"
              size="small"
              withLoneIcon
              aria-label="Close"
              className="mAn mLa"
            >
              <SvgIcon iconName="close" size="small" />
            </Dialog.Close>
          </Dialog.Header>
          <Dialog.Body>
            <Flex
              main={'space-between'}
              css={{ minHeight: 'max-content', alignItems: 'stretch' }}
              gap="spacingXl"
            >
              <FlexItem grow="1" css={{ width: '50%' }}>
                <Flex flow="column">
                  <Box css={{ color: '$colorAccent70', fontWeight: 'bolder ' }}>
                    {'Calculation Set Values'}
                  </Box>
                  <DataList align="right" ratio={[3, 1]} css={{ margin: 0 }}>
                    <DataList.Row>
                      <DataList.Label>{'Substation name'}</DataList.Label>
                      <DataList.Value>
                        {calculationSetStatistics.statistics.substationName}
                      </DataList.Value>
                    </DataList.Row>
                    <DataList.Row>
                      <DataList.Label>{'Feeding transformer'}</DataList.Label>
                      <DataList.Value>
                        {
                          calculationSetStatistics.statistics
                            .feedingTransformerName
                        }
                      </DataList.Value>
                    </DataList.Row>
                  </DataList>
                  <Separator />
                  <DataList align="right" ratio={[3, 1]} css={{ margin: 0 }}>
                    <DataList.Row>
                      <DataList.Label>
                        {'Total calculated grid elements'}
                      </DataList.Label>
                      <DataList.Value>
                        {calculationSetStatistics.statistics.totalGridElements}
                      </DataList.Value>
                    </DataList.Row>
                    <DataList.Row>
                      <DataList.Label>
                        {'Number of ACline segments'}
                      </DataList.Label>
                      <DataList.Value>
                        {calculationSetStatistics.statistics.acLinesCount}
                      </DataList.Value>
                    </DataList.Row>
                    <DataList.Row>
                      <DataList.Label>
                        {'Number of energy consumers'}
                      </DataList.Label>
                      <DataList.Value>
                        {
                          calculationSetStatistics.statistics
                            .energyConsumersCount
                        }
                      </DataList.Value>
                    </DataList.Row>
                    <DataList.Row>
                      <DataList.Label>{'Number of cabinets'}</DataList.Label>
                      <DataList.Value>
                        {calculationSetStatistics.statistics.cabinetsCount}
                      </DataList.Value>
                    </DataList.Row>
                  </DataList>
                  <Separator />
                  <GridVersionSection gridVersionName={gridVersionName} />
                </Flex>
              </FlexItem>
              <FlexItem>
                <Separator orientation="vertical" className="h100" />
              </FlexItem>
              <FlexItem
                as={Flex}
                grow="1"
                flex="auto"
                className="h100"
                flow="column"
                gap={'spacingXl'}
              >
                <Flex flow={'column'}>
                  <Box css={{ color: '$colorAccent70', fontWeight: 'bolder ' }}>
                    {'Calculations included in the set'}
                  </Box>
                  <Flex flow={'column'} className="mLm">
                    <CollapsibleContainer>
                      {(toggle, currentState) => (
                        <Box>
                          <Flex
                            cross="center"
                            css={{ fontWeight: 'bolder' }}
                            onClick={() => toggle()}
                          >
                            <SvgIcon
                              iconName={
                                currentState === 'EXPANDED'
                                  ? 'chevronDown'
                                  : 'chevronUp'
                              }
                            />
                            <span>{'Load Flow'}</span>
                          </Flex>
                          <Flex
                            flow={'column'}
                            gap={'spacingXs'}
                            css={{ marginLeft: '$spacingXl' }}
                            className={
                              isVisible(currentState)
                                ? 'is-visible'
                                : 'is-hidden'
                            }
                          >
                            {calculationSetStatistics.statistics.calculationsIncluded.loadFlow.map(
                              scenarioName => (
                                <Box className="is-actionable">
                                  {scenarioName}
                                </Box>
                              )
                            )}
                          </Flex>
                        </Box>
                      )}
                    </CollapsibleContainer>
                    <CollapsibleContainer>
                      {(toggle, currentState) => (
                        <Box>
                          <Flex
                            cross="center"
                            css={{ fontWeight: 'bolder' }}
                            onClick={() => toggle()}
                          >
                            <SvgIcon
                              iconName={
                                currentState === 'EXPANDED'
                                  ? 'chevronDown'
                                  : 'chevronUp'
                              }
                            />
                            <span>{'Short Circuit'}</span>
                          </Flex>
                          <Flex
                            flow={'column'}
                            gap={'spacingXs'}
                            css={{ marginLeft: '$spacingXl' }}
                            className={
                              isVisible(currentState)
                                ? 'is-visible'
                                : 'is-hidden'
                            }
                          >
                            {calculationSetStatistics.statistics.calculationsIncluded.shortCircuit.map(
                              scenarioName => (
                                <Box className="is-actionable">
                                  {scenarioName}
                                </Box>
                              )
                            )}
                          </Flex>
                        </Box>
                      )}
                    </CollapsibleContainer>
                  </Flex>
                </Flex>
                <Flex flow="column" main={'space-between'}>
                  <Box css={{ color: '$colorAccent70', fontWeight: 'bolder ' }}>
                    {'Processing times'}
                  </Box>
                  <DataList align="right" ratio={[2, 1]} css={{ margin: 0 }}>
                    {calculationSetStatistics.statistics.processingTimes
                      .gridDataRetrieval.type ===
                      'PROCESSING_TIME_AVAILABLE' && (
                      <DataList.Row>
                        <DataList.Label>{'Grid data retrieval'}</DataList.Label>
                        <DataList.Value>
                          {calculationSetStatistics.statistics.processingTimes.gridDataRetrieval.processingTime.toSecondsText()}
                        </DataList.Value>
                      </DataList.Row>
                    )}
                    {calculationSetStatistics.statistics.processingTimes
                      .gridContainmentRetrieval.type ===
                      'PROCESSING_TIME_AVAILABLE' && (
                      <DataList.Row>
                        <DataList.Label>
                          {'Grid containment retrieval'}
                        </DataList.Label>
                        <DataList.Value>
                          {calculationSetStatistics.statistics.processingTimes.gridContainmentRetrieval.processingTime.toSecondsText()}
                        </DataList.Value>
                      </DataList.Row>
                    )}
                    {calculationSetStatistics.statistics.processingTimes
                      .calculation.type === 'PROCESSING_TIME_AVAILABLE' && (
                      <DataList.Row>
                        <DataList.Label>{'Calculation'}</DataList.Label>
                        <DataList.Value>
                          {calculationSetStatistics.statistics.processingTimes.calculation.processingTime.toSecondsText()}
                        </DataList.Value>
                      </DataList.Row>
                    )}
                  </DataList>
                </Flex>
              </FlexItem>
            </Flex>
          </Dialog.Body>
        </Dialog.Box>
      </Dialog>
    </span>
  );
}
export function ShareExecutionButton(props: {
  className?: string;
  buttonClassName?: string;
  shareCalculationSetExecution: () => void;
}) {
  const isSharingExecutionEnabled =
    useAppState().featureToggles?.isSharingExecutionEnabled;
  if (!isSharingExecutionEnabled) return null;
  return (
    <Flex main={'center'} cross="center" className={props.className || ''}>
      <Button
        leftIcon={<SvgIcon iconName="share" />}
        variant="outline"
        size="small"
        className={`mAn ${props.buttonClassName}`}
        onClick={() => props.shareCalculationSetExecution()}
      >
        {'Share execution'}
      </Button>
    </Flex>
  );
}
